import { AxiosRequestConfig } from 'axios';
import { useRecoilValue } from 'recoil';
import { useAppBridge } from '@shopify/app-bridge-react';
import { Redirect as ShopifyRedirect } from '@shopify/app-bridge/actions';
import {
  accessTokenInvalidatedState,
  accessTokenSelector,
  sessionSelector,
} from '@states';
import { useModal } from '@hooks';
import { useCallback, useEffect } from 'react';
import { AccessTokenPayload, AuthContext } from 'src/types';
import jwtDecode from 'jwt-decode';
import * as Sentry from '@sentry/browser';

export const useAuthContext = (): AuthContext | undefined => {
  const accessToken = useRecoilValue(accessTokenSelector) as string;
  try {
    const payload = jwtDecode<AccessTokenPayload>(accessToken);
    const { accountGid, exp, roles } = payload;

    if (!accountGid) {
      throw new Error('AuthContext does not contain accountGid');
    }

    return {
      accessToken,
      accountGid,
      isExpired: (exp as number) < Math.floor(Date.now() / 1000),
      roles,
    };
  } catch (error) {
    console.error('useAuthContext ERROR', error);
    Sentry.captureException(error);
  }
};

export const useCurrentSession = () => {
  return useRecoilValue(sessionSelector);
};

export const useAxiosAuthToken = () => {
  const authContext = useAuthContext();
  const axiosConfig: AxiosRequestConfig = {
    headers: {
      Authorization: `Bearer ${authContext?.accessToken}`,
    },
  };
  return axiosConfig;
};

export const useSessionExpiredCheck = () => {
  const authContext = useAuthContext();
  const shopifyAppBridge = useAppBridge();
  const session = useCurrentSession();
  const modal = useModal();
  const accessTokenInvalidated = useRecoilValue(accessTokenInvalidatedState);

  const redirect = ShopifyRedirect.create(shopifyAppBridge as any);
  const onClose = useCallback(() => {
    redirect.dispatch(
      ShopifyRedirect.Action.APP,
      `/auth?shop=${session?.shop}`,
    );
  }, [redirect, session?.shop]);

  useEffect(() => {
    if ((authContext && authContext.isExpired) || accessTokenInvalidated) {
      modal.display(() => 'Your session is expired. Please reload the app.', {
        onClose,
        primaryAction: {
          content: 'Reload',
          onAction: onClose,
        },
        title: 'Attention',
      });
    }
  }, [
    authContext?.isExpired,
    accessTokenInvalidated,
    authContext,
    modal,
    onClose,
  ]);
};

export const useIsImpersonator = () => {
  const authContext = useAuthContext();
  return authContext?.roles.includes('impersonator');
};
